/**
 * Suppliers Management Hook
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 1: Suppliers Management
 *
 * Provides hooks for:
 * - Fetching suppliers list
 * - Creating new suppliers
 * - Updating existing suppliers
 * - Archiving suppliers (soft delete)
 */

import { useState, useEffect, useCallback } from 'react'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/suppliers`

/**
 * Call the suppliers edge function
 */
async function callSuppliersFunction(action, data = {}) {
  const response = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY
    },
    body: JSON.stringify({ action, ...data }),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || result.message || 'Request failed')
  }

  return result.data
}

/**
 * Main hook for suppliers management
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.activeOnly - If true, only fetch active suppliers (default: false)
 * @returns {Object} - suppliers, isLoading, error, createSupplier, updateSupplier, deleteSupplier, refetch
 */
export function useSuppliers(options = {}) {
  const { activeOnly = false } = options

  const [suppliers, setSuppliers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Fetch suppliers from the API
   */
  const fetchSuppliers = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const params = activeOnly ? { active: true } : {}
      const data = await callSuppliersFunction('list', params)

      setSuppliers(data || [])
    } catch (err) {
      console.error('Error fetching suppliers:', err)
      setError(err.message)
      setSuppliers([])
    } finally {
      setIsLoading(false)
    }
  }, [activeOnly])

  /**
   * Create a new supplier
   *
   * @param {Object} supplierData - Supplier data
   * @param {string} supplierData.name - Supplier name (required)
   * @param {string} supplierData.contact_name - Contact name
   * @param {string} supplierData.email - Email address
   * @param {string} supplierData.phone - Phone number
   * @param {string} supplierData.website - Website URL
   * @param {number} supplierData.lead_time_days - Lead time in days
   * @param {string} supplierData.notes - Additional notes
   * @returns {Promise<Object>} - Created supplier
   */
  const createSupplier = useCallback(async (supplierData) => {
    try {
      setError(null)
      const newSupplier = await callSuppliersFunction('create', supplierData)

      // Add to local state
      setSuppliers(prev => [...prev, newSupplier].sort((a, b) =>
        a.name.localeCompare(b.name)
      ))

      return newSupplier
    } catch (err) {
      console.error('Error creating supplier:', err)
      setError(err.message)
      throw err
    }
  }, [])

  /**
   * Update an existing supplier
   *
   * @param {string} id - Supplier ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Updated supplier
   */
  const updateSupplier = useCallback(async (id, updates) => {
    try {
      setError(null)
      const updatedSupplier = await callSuppliersFunction('update', { id, ...updates })

      // Update local state
      setSuppliers(prev =>
        prev.map(s => s.id === id ? updatedSupplier : s)
          .sort((a, b) => a.name.localeCompare(b.name))
      )

      return updatedSupplier
    } catch (err) {
      console.error('Error updating supplier:', err)
      setError(err.message)
      throw err
    }
  }, [])

  /**
   * Archive a supplier (soft delete)
   *
   * @param {string} id - Supplier ID
   * @returns {Promise<Object>} - Archived supplier
   */
  const deleteSupplier = useCallback(async (id) => {
    try {
      setError(null)
      const archivedSupplier = await callSuppliersFunction('delete', { id })

      // Update local state - if activeOnly, remove from list; otherwise update
      if (activeOnly) {
        setSuppliers(prev => prev.filter(s => s.id !== id))
      } else {
        setSuppliers(prev =>
          prev.map(s => s.id === id ? archivedSupplier : s)
        )
      }

      return archivedSupplier
    } catch (err) {
      console.error('Error archiving supplier:', err)
      setError(err.message)
      throw err
    }
  }, [activeOnly])

  /**
   * Restore an archived supplier
   *
   * @param {string} id - Supplier ID
   * @returns {Promise<Object>} - Restored supplier
   */
  const restoreSupplier = useCallback(async (id) => {
    try {
      setError(null)
      const restoredSupplier = await callSuppliersFunction('update', { id, active: true })

      // Update local state
      setSuppliers(prev =>
        prev.map(s => s.id === id ? restoredSupplier : s)
          .sort((a, b) => a.name.localeCompare(b.name))
      )

      return restoredSupplier
    } catch (err) {
      console.error('Error restoring supplier:', err)
      setError(err.message)
      throw err
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    fetchSuppliers()
  }, [fetchSuppliers])

  return {
    suppliers,
    isLoading,
    error,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    restoreSupplier,
    refetch: fetchSuppliers,
  }
}

/**
 * Hook for fetching a single supplier
 *
 * @param {string} supplierId - Supplier ID
 * @returns {Object} - supplier, isLoading, error, refetch
 */
export function useSupplier(supplierId) {
  const [supplier, setSupplier] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSupplier = useCallback(async () => {
    if (!supplierId) {
      setSupplier(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const data = await callSuppliersFunction('get', { id: supplierId })
      setSupplier(data)
    } catch (err) {
      console.error('Error fetching supplier:', err)
      setError(err.message)
      setSupplier(null)
    } finally {
      setIsLoading(false)
    }
  }, [supplierId])

  useEffect(() => {
    fetchSupplier()
  }, [fetchSupplier])

  return { supplier, isLoading, error, refetch: fetchSupplier }
}

export default useSuppliers
